/**
 * 画布数据压缩/解压工具模块
 *
 * 【设计目标】
 * 解决画布数据上传到服务器时体积过大的问题（1000个元素约1.1MB）
 * 通过剔除 Fabric.js 默认值、优化数字精度等方式，预期压缩率 60-70%
 *
 * 【核心策略】
 * 1. 默认值剔除：只保存与默认值不同的字段
 * 2. 数字精度优化：坐标保留1位小数，缩放保留2位小数
 * 3. 空值过滤：剔除 null、空字符串、空数组
 * 4. 向后兼容：支持解压旧版本未压缩的数据
 *
 * 【压缩流程】
 * 保存时: Canvas → toJSON() → compressCanvasData() → 服务器
 * 加载时: 服务器 → decompressCanvasData() → enlivenObjects() → Canvas
 */

import { FabricCanvasData } from "@xmiles/holopix-types";

// ==================== 类型定义 ====================

/**
 * 画布对象类型
 */
export type CanvasObjectType = "image" | "group" | "rect" | "text" | "path";

/**
 * 画布对象数据接口
 */
export interface CanvasObjectData {
  type: CanvasObjectType;
  id?: string;
  name?: string;
  left?: number;
  top?: number;
  width?: number;
  height?: number;
  scaleX?: number;
  scaleY?: number;
  angle?: number;
  opacity?: number;
  src?: string;
  originalSrc?: string;
  clientId?: string;
  resultId?: string;
  isPlaceholder?: boolean;
  placeholderType?: string;
  placeholderStatus?: string;
  isEnhancingPromptPlaceholder?: boolean;
  isPostProcessResult?: boolean;
  canvasInsertionOrder?: number;
  [key: string]: unknown;
}

/**
 * 画布数据接口
 */
export interface CanvasData {
  objects: any[];
  viewport?: {
    zoom: number;
    pan: { x: number; y: number };
  };
  id?: number;
  [key: string]: unknown;
}

/**
 * 压缩后的画布数据接口
 */
export interface CompressedCanvasData extends CanvasData {
  _compressed: true;
  _version: string;
  _timestamp: number;
}

// ==================== 默认值配置 ====================

/**
 * Fabric.js Image 对象默认值映射表
 * 基于 Fabric.js 5.5.2 版本的默认值
 *
 * 【维护说明】
 * - 当升级 Fabric.js 版本时，需要同步更新此表
 * - 新增字段需要补充默认值
 * - 修改默认值需要评估兼容性
 */
const FABRIC_IMAGE_DEFAULTS: Record<string, unknown> = {
  // 基础属性
  version: "5.5.2",
  originX: "left",
  originY: "top",
  fill: "rgb(0,0,0)",
  stroke: null,
  strokeWidth: 0,
  strokeDashArray: null,
  strokeLineCap: "butt",
  strokeDashOffset: 0,
  strokeLineJoin: "miter",
  strokeUniform: false,
  strokeMiterLimit: 4,
  scaleX: 1,
  scaleY: 1,
  angle: 0,
  flipX: false,
  flipY: false,
  opacity: 1,
  shadow: null,
  visible: true,
  backgroundColor: "",
  fillRule: "nonzero",
  paintFirst: "fill",
  globalCompositeOperation: "source-over",
  skewX: 0,
  skewY: 0,

  // 图片特有属性
  cropX: 0,
  cropY: 0,
  crossOrigin: null,
  alignX: "none",
  alignY: "none",
  meetOrSlice: "meet",

  // 交互属性
  selectable: true,
  evented: true,
  lockMovementX: false,
  lockMovementY: false,
  lockRotation: false,
  lockScalingX: false,
  lockScalingY: false,
  lockUniScaling: false,
  lockScalingFlip: false,
  hasControls: true,
  hasBorders: true,
  hasRotatingPoint: true,
  transparentCorners: true,
  cornerSize: 13,
  borderColor: "rgb(178,204,255)",
  cornerColor: "rgb(178,204,255)",
  cornerStrokeColor: "",
  cornerStyle: "rect",
  borderScaleFactor: 1,
  borderOpacityWhenMoving: 0.4,
  borderDashArray: null,
  perPixelTargetFind: false,

  // 其他属性
  clipTo: null,
  filters: [],
  resizeFilters: [],
  erasable: false
};

/**
 * Fabric.js Group 对象默认值映射表
 */
const FABRIC_GROUP_DEFAULTS: Record<string, unknown> = {
  ...FABRIC_IMAGE_DEFAULTS,
  // Group 特有默认值
  subTargetCheck: false,
  interactive: false
};

/**
 * Fabric.js Rect 对象默认值映射表
 */
const FABRIC_RECT_DEFAULTS: Record<string, unknown> = {
  ...FABRIC_IMAGE_DEFAULTS,
  rx: 0,
  ry: 0
};

/**
 * Fabric.js Text/Textbox/IText 对象默认值映射表
 *
 * 【关键字段】styles 必须保留：空数组样式在压缩时会被剔除，
 * 若解压后缺少 styles，fabric.Text.fromObject 会把文字对象的 styles 置为 undefined，
 * 导致后续序列化 stylesToArray 崩溃（"Cannot read properties of undefined (reading '0')"）。
 */
const FABRIC_TEXT_DEFAULTS: Record<string, unknown> = {
  ...FABRIC_IMAGE_DEFAULTS,
  styles: []
};

/**
 * 根据对象类型获取默认值
 */
const getDefaultsByType = (type: string): Record<string, unknown> => {
  switch (type) {
    case "image":
      return FABRIC_IMAGE_DEFAULTS;
    case "group":
      return FABRIC_GROUP_DEFAULTS;
    case "rect":
      return FABRIC_RECT_DEFAULTS;
    case "text":
    case "i-text":
    case "textbox":
      return FABRIC_TEXT_DEFAULTS;
    default:
      return FABRIC_IMAGE_DEFAULTS;
  }
};

/**
 * 必须保留的核心字段（即使等于默认值）
 * 这些字段是业务逻辑必需的
 */
const CORE_FIELDS = new Set<string>([
  // 标识字段
  "type",
  "id",
  "name",

  // 位置和尺寸（即使为0也需要保留）
  "left",
  "top",
  "width",
  "height",

  // 图片特有
  "src",
  "originalSrc",

  // 生图相关
  "clientId",
  "resultId",

  // 占位符相关
  "isPlaceholder",
  "placeholderType",
  "placeholderStatus",
  "isEnhancingPromptPlaceholder",
  "isPostProcessResult",
  "canvasInsertionOrder",
]);

/**
 * 需要精度优化的数字字段
 */
const PRECISION_CONFIG: Record<string, number> = {
  // 坐标：保留1位小数（0.1像素精度足够）
  left: 1,
  top: 1,

  // 缩放：保留2位小数
  scaleX: 2,
  scaleY: 2,

  // 角度：保留整数
  angle: 0,

  // 透明度：保留2位小数
  opacity: 2,

  // 裁剪：保留整数
  cropX: 0,
  cropY: 0,

  // 倾斜：保留1位小数
  skewX: 1,
  skewY: 1
};

// ==================== 压缩函数 ====================

/**
 * 优化数字精度
 * @param value 原始数值
 * @param precision 保留小数位数
 * @returns 优化后的数值
 */
const optimizeNumber = (value: number, precision: number): number => {
  if (precision === 0) {
    return Math.round(value);
  }
  const factor = Math.pow(10, precision);
  return Math.round(value * factor) / factor;
};

/**
 * 压缩单个画布对象
 *
 * 【压缩逻辑】
 * 1. 遍历对象所有字段
 * 2. 核心字段无条件保留
 * 3. 非核心字段与默认值比较，相同则剔除
 * 4. 数字字段进行精度优化
 * 5. 空数组、空字符串、null 剔除
 *
 * @param obj 原始画布对象数据
 * @returns 压缩后的对象数据
 */
export const compressCanvasObject = (obj: CanvasObjectData): Partial<CanvasObjectData> => {
  if (!obj || typeof obj !== "object") return obj;

  const compressed: Partial<CanvasObjectData> = {};
  const defaults = getDefaultsByType(obj.type);

  for (const [key, value] of Object.entries(obj)) {
    // 跳过内部标记字段
    if (key.startsWith("__")) continue;

    // 核心字段必须保留
    if (CORE_FIELDS.has(key)) {
      // 数字字段进行精度优化
      if (typeof value === "number" && PRECISION_CONFIG[key] !== undefined) {
        (compressed as Record<string, unknown>)[key] = optimizeNumber(value, PRECISION_CONFIG[key]);
      } else {
        (compressed as Record<string, unknown>)[key] = value;
      }
      continue;
    }

    // 剔除 undefined
    if (value === undefined) continue;

    // 剔除 null
    if (value === null) continue;

    // 剔除空字符串
    if (value === "") continue;

    // 剔除空数组
    if (Array.isArray(value) && value.length === 0) continue;

    // 剔除空对象
    if (typeof value === "object" && !Array.isArray(value) && value !== null) {
      if (Object.keys(value).length === 0) continue;
    }

    // 与默认值比较
    const defaultValue = defaults[key];
    if (value === defaultValue) continue;

    // 数字字段进行精度优化
    if (typeof value === "number" && PRECISION_CONFIG[key] !== undefined) {
      const optimized = optimizeNumber(value, PRECISION_CONFIG[key]);
      // 优化后如果等于默认值，则剔除
      if (optimized === defaultValue) continue;
      (compressed as Record<string, unknown>)[key] = optimized;
      continue;
    }

    // 递归处理嵌套对象（如 clipPath）
    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      // 保留 fabric 特定对象的原始结构
      if ((value as CanvasObjectData).type && typeof (value as CanvasObjectData).type === "string") {
        // 可能是嵌套的 fabric 对象，递归压缩
        const nestedCompressed = compressCanvasObject(value as CanvasObjectData);
        if (Object.keys(nestedCompressed).length > 0) {
          (compressed as Record<string, unknown>)[key] = nestedCompressed;
        }
        continue;
      }
    }

    // 保留非默认值
    (compressed as Record<string, unknown>)[key] = value;
  }

  return compressed;
};

/**
 * 解压单个画布对象
 *
 * 【解压逻辑】
 * 1. 根据对象类型获取默认值
 * 2. 合并默认值和压缩数据
 * 3. 确保核心字段存在
 *
 * @param compressed 压缩后的对象数据
 * @returns 完整的画布对象数据
 */
export const decompressCanvasObject = (compressed: Partial<CanvasObjectData>): CanvasObjectData => {
  if (!compressed || typeof compressed !== "object") return compressed as CanvasObjectData;

  const defaults = getDefaultsByType(compressed.type || "image");

  // 合并默认值和压缩数据
  const decompressed: CanvasObjectData = {
    ...(defaults as CanvasObjectData),
    ...compressed
  };

  // 递归解压嵌套对象
  if (decompressed.clipPath && typeof decompressed.clipPath === "object") {
    decompressed.clipPath = decompressCanvasObject(decompressed.clipPath as Partial<CanvasObjectData>);
  }

  // 递归解压 group 的 objects
  if (decompressed.objects && Array.isArray(decompressed.objects)) {
    decompressed.objects = decompressed.objects.map((obj: unknown) => decompressCanvasObject(obj as Partial<CanvasObjectData>));
  }

  return decompressed;
};

/**
 * 压缩画布数据（用于保存到服务器）
 *
 * 【处理流程】
 * 1. 压缩所有对象
 * 2. 添加压缩标记
 * 3. 保留其他画布属性（viewport 等）
 *
 * @param canvasData 原始画布数据
 * @returns 压缩后的画布数据
 */
export const compressCanvasData = (canvasData: CanvasData): CompressedCanvasData => {
  if (!canvasData || typeof canvasData !== "object") {
    return canvasData as CompressedCanvasData;
  }

  const compressed: CompressedCanvasData = {
    ...canvasData,
    objects: Array.isArray(canvasData.objects) ? canvasData.objects.map(compressCanvasObject) : canvasData.objects,
    // 添加压缩标记
    _compressed: true,
    _version: "1.0",
    _timestamp: Date.now()
  };

  return compressed;
};

/**
 * 解压画布数据（用于从服务器加载）
 *
 * 【兼容性处理】
 * - 如果数据没有 _compressed 标记，直接返回（向后兼容）
 * - 如果版本不匹配，尝试解压（向前兼容）
 *
 * @param canvasData 画布数据（可能是压缩或未压缩的）
 * @returns 解压后的完整画布数据
 */
export const decompressCanvasData = (canvasData: FabricCanvasData | Record<string, any>): CanvasData => {
  if (!canvasData || typeof canvasData !== "object") {
    return canvasData as CanvasData;
  }

  // 未压缩的数据直接返回（向后兼容）
  if (!(canvasData as CompressedCanvasData)._compressed) {
    return canvasData as CanvasData;
  }

  const compressed = canvasData as CompressedCanvasData;
  const decompressed: CanvasData = {
    ...compressed,
    objects: Array.isArray(compressed.objects)
      ? compressed.objects.map((obj: unknown) => decompressCanvasObject(obj as Partial<CanvasObjectData>))
      : compressed.objects
  };

  // 删除压缩标记（可选，保留也可以用于调试）
  // delete (decompressed as Partial<CompressedCanvasData>)._compressed;
  // delete (decompressed as Partial<CompressedCanvasData>)._version;
  // delete (decompressed as Partial<CompressedCanvasData>)._timestamp;

  return decompressed;
};

// ==================== 工具函数 ====================

/**
 * 计算压缩率
 *
 * @param original 原始数据
 * @param compressed 压缩后数据
 * @returns 压缩率百分比（如 65 表示压缩了65%）
 */
export const calculateCompressionRatio = (original: CanvasData, compressed: CompressedCanvasData): number => {
  try {
    const originalStr = JSON.stringify(original);
    const compressedStr = JSON.stringify(compressed);

    const originalSize = new Blob([originalStr]).size;
    const compressedSize = new Blob([compressedStr]).size;

    return Math.round(((originalSize - compressedSize) / originalSize) * 100);
  } catch (e) {
    console.warn("[canvasDataCompress] 计算压缩率失败:", e);
    return 0;
  }
};

/**
 * 获取数据大小（字节）
 *
 * @param data 任意数据
 * @returns 字节大小
 */
export const getDataSize = (data: unknown): number => {
  try {
    const str = JSON.stringify(data);
    return new Blob([str]).size;
  } catch (e) {
    return 0;
  }
};

/**
 * 格式化数据大小为可读字符串
 *
 * @param bytes 字节数
 * @returns 格式化后的字符串（如 "1.5 KB"）
 */
export const formatDataSize = (bytes: number): string => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

/**
 * 日志记录压缩信息（调试用）
 *
 * @param original 原始数据
 * @param compressed 压缩后数据
 * @param context 上下文信息
 */
export const logCompressionInfo = (original: CanvasData, compressed: CompressedCanvasData, context?: string): void => {
  const originalSize = getDataSize(original);
  const compressedSize = getDataSize(compressed);
  const ratio = calculateCompressionRatio(original, compressed);

  const prefix = context ? `[${context}] ` : "";
  console.log(
    `${prefix}画布数据压缩: ${formatDataSize(originalSize)} → ${formatDataSize(compressedSize)} ` +
      `(节省 ${ratio}%, 对象数: ${original.objects?.length || 0})`
  );
};
